import React, { useEffect, useCallback, useState } from "react"
import { Modal } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { Loader } from "shared/ui/Loader/Loader.component"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { useExportMap } from "features/QueryConsole/api/useExportMap"
import { useExportMapStore } from "stores/exportMapStore"
import { useProjectStore } from "stores/projectStore"
import { QueryInstance } from "types/engine.types"
import { EXPORT_VARIABLE_TYPES } from "types/engineExport.types"
import { ExportMapProfile } from "types/exportMap.types"
import {
  parseExportMapCSV,
  groupVariables,
  exportMapToCSV,
} from "./utils/exportMapParser"
import { mergeWithOriginalMap } from "./utils/exportMapMerger"
import ExportMapEditor from "./ExportMapEditor.component"

interface ExportMapModalProps {
  open: boolean
  onClose: () => void
  queryInstance: QueryInstance | null
  onExport: (exportMapCsv: string) => Promise<void>
  /** Variable naming method: "numbering" (A1, A2) or "labels" (actual labels) */
  varType?: EXPORT_VARIABLE_TYPES
}

/**
 * Modal for configuring export map before SPSS/CSV export.
 * Auto-generates export map when opened and allows editing variable codes.
 */
const ExportMapModal: React.FC<ExportMapModalProps> = ({
  open,
  onClose,
  queryInstance,
  onExport,
  varType = EXPORT_VARIABLE_TYPES.NUMBERING,
}) => {
  const { project } = useProjectStore()
  const {
    generateMap,
    isGeneratingMap,
    listProfiles,
    getOriginalMap,
    saveProfile,
    isSavingProfile,
  } = useExportMap()

  const {
    setVariables,
    setGroups,
    setRootVariables,
    setAvailableProfiles,
    availableProfiles,
    selectedProfile,
    setSelectedProfile,
    setOriginalCsv,
    setOriginalMapCsv,
    setIsLoading,
    isLoading,
    hasChanges,
    setHasChanges,
    allVariables,
    reset,
    getMaxMultiValidationErrors,
  } = useExportMapStore()

  const [isExporting, setIsExporting] = useState(false)
  const [saveProfileModalOpen, setSaveProfileModalOpen] = useState(false)
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false)

  /**
   * Load export map data when modal opens
   * Uses parallel API calls for better performance
   */
  const loadExportMap = useCallback(async () => {
    if (!queryInstance || !open) return

    setIsLoading(true)

    try {
      // Run all API calls in parallel for faster loading
      const [mapResponse, originalMapResult, profilesResult] =
        await Promise.all([
          generateMap(queryInstance, varType),
          getOriginalMap(project.id).catch(() => ({ data: "" })),
          listProfiles(project.id).catch(() => ({ data: [] })),
        ])

      const generatedCsv = mapResponse.data
      setOriginalCsv(generatedCsv)

      const originalMapCsv = originalMapResult.data || ""
      setOriginalMapCsv(originalMapCsv)

      setAvailableProfiles(profilesResult.data || [])

      // Parse the generated CSV
      const generatedVariables = parseExportMapCSV(generatedCsv)

      // Merge with original codes (using current useOriginalCodes setting from store)
      const currentUseOriginalCodes =
        useExportMapStore.getState().useOriginalCodes
      const mergeResult = mergeWithOriginalMap(
        generatedVariables,
        originalMapCsv,
        currentUseOriginalCodes
      )
      setVariables(mergeResult.variables)

      // Group variables for tree display
      const { groups, rootVariables } = groupVariables(mergeResult.variables)
      setGroups(groups)
      setRootVariables(rootVariables)
    } catch (error) {
      console.error("Failed to load export map:", error)
      notify.toast.error("Failed to generate export map")
    } finally {
      setIsLoading(false)
    }
  }, [
    queryInstance,
    open,
    varType,
    generateMap,
    getOriginalMap,
    listProfiles,
    project.id,
    setVariables,
    setGroups,
    setRootVariables,
    setAvailableProfiles,
    setOriginalCsv,
    setOriginalMapCsv,
    setIsLoading,
  ])

  useEffect(() => {
    if (open) {
      loadExportMap()
    } else {
      reset()
    }
  }, [open, loadExportMap, reset])

  /**
   * Handle applying export map and triggering export
   */
  const handleApplyAndExport = useCallback(async () => {
    // Validate max-multi before export
    const maxMultiErrors = getMaxMultiValidationErrors()
    if (maxMultiErrors.size > 0) {
      const firstErrors = Array.from(maxMultiErrors.values())[0]
      notify.toast.error(`Max-Multi validation: ${firstErrors[0]}`)
      return
    }

    const csv = exportMapToCSV(allVariables)
    setIsExporting(true)
    try {
      await onExport(csv)
      onClose()
    } catch (error) {
      console.error("Export failed:", error)
      notify.toast.error("Export failed")
    } finally {
      setIsExporting(false)
    }
  }, [allVariables, onExport, onClose, getMaxMultiValidationErrors])

  /**
   * Handle saving current map as profile then exporting.
   * If no profile is selected, opens modal to create new profile.
   */
  const handleSaveAndExport = useCallback(async () => {
    // Validate max-multi before export
    const maxMultiErrors = getMaxMultiValidationErrors()
    if (maxMultiErrors.size > 0) {
      const firstErrors = Array.from(maxMultiErrors.values())[0]
      notify.toast.error(`Max-Multi validation: ${firstErrors[0]}`)
      return
    }

    if (!selectedProfile) {
      // No profile selected - prompt user to create one
      setSaveProfileModalOpen(true)
      return
    }

    const csv = exportMapToCSV(allVariables)

    try {
      await saveProfile(project.id, selectedProfile.name, csv)
      notify.toast.success(`Profile "${selectedProfile.name}" saved`)
      setHasChanges(false)
      setIsExporting(true)
      await onExport(csv)
      onClose()
    } catch (error) {
      console.error("Failed to save/export:", error)
      notify.toast.error("Failed to save export map")
    } finally {
      setIsExporting(false)
    }
  }, [
    allVariables,
    saveProfile,
    project.id,
    onExport,
    onClose,
    selectedProfile,
    setHasChanges,
    getMaxMultiValidationErrors,
  ])

  /**
   * Handle creating new profile and exporting
   */
  const handleCreateProfileAndExport = useCallback(
    async (profileName: string) => {
      // Validate max-multi before export
      const maxMultiErrors = getMaxMultiValidationErrors()
      if (maxMultiErrors.size > 0) {
        const firstErrors = Array.from(maxMultiErrors.values())[0]
        notify.toast.error(`Max-Multi validation: ${firstErrors[0]}`)
        return
      }

      const csv = exportMapToCSV(allVariables)
      const sanitizedName = profileName.toLowerCase().replace(/\s+/g, "_")

      try {
        await saveProfile(project.id, sanitizedName, csv)

        // Add new profile to list and select it
        const newProfile: ExportMapProfile = {
          name: sanitizedName,
          filename: `$exportmap.${sanitizedName}.csv`,
        }
        setAvailableProfiles([...availableProfiles, newProfile])
        setSelectedProfile(newProfile)
        setHasChanges(false)

        notify.toast.success(`Profile "${profileName}" created`)
        setSaveProfileModalOpen(false)

        // Now export
        setIsExporting(true)
        await onExport(csv)
        onClose()
      } catch (error) {
        console.error("Failed to create profile/export:", error)
        notify.toast.error("Failed to create profile")
        throw error // Re-throw so ExportModal shows error
      } finally {
        setIsExporting(false)
      }
    },
    [
      allVariables,
      saveProfile,
      project.id,
      availableProfiles,
      setAvailableProfiles,
      setSelectedProfile,
      setHasChanges,
      onExport,
      onClose,
      getMaxMultiValidationErrors,
    ]
  )

  /**
   * Handle cancel with unsaved changes warning
   */
  const handleCancel = useCallback(() => {
    if (hasChanges) {
      setUnsavedChangesModalOpen(true)
    } else {
      onClose()
    }
  }, [hasChanges, onClose])

  /**
   * Handle confirming close with unsaved changes
   */
  const handleConfirmClose = useCallback(() => {
    setUnsavedChangesModalOpen(false)
    onClose()
  }, [onClose])

  return (
    <>
      <Modal
        title="Configure Export Map"
        open={open}
        onCancel={handleCancel}
        width="95vw"
        style={{ maxWidth: 1600, top: 20 }}
        styles={{
          body: {
            padding: "12px 24px",
            maxHeight: "calc(100vh - 120px)",
            overflow: "hidden",
          },
        }}
        footer={null}
        destroyOnClose
      >
        {isLoading || isGeneratingMap || isExporting ? (
          <div
            className="flex flex-col items-center justify-center"
            style={{ height: "calc(100vh - 200px)" }}
          >
            <Loader fontSize={48} color="#36c3ed" />
            <p className="mt-4 text-gray-500">
              {isExporting ? "Exporting data..." : "Loading export map..."}
            </p>
          </div>
        ) : (
          <ExportMapEditor
            onCancel={handleCancel}
            onApply={handleApplyAndExport}
            onSaveAndExport={handleSaveAndExport}
            onRegenerate={loadExportMap}
            isSaving={isSavingProfile}
            maxHeight="calc(100vh - 200px)"
          />
        )}
      </Modal>

      {/* Save New Profile Modal */}
      <ExportModal
        title="Create New Profile"
        isVisible={saveProfileModalOpen}
        cancel={() => setSaveProfileModalOpen(false)}
        confirm={(profileName) => handleCreateProfileAndExport(profileName)}
        message="Enter a name for this export map profile. Profile names are converted to lowercase with underscores."
        ExportButtonText="Save & Export"
      />

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationModal
        title="Unsaved Changes"
        isVisible={unsavedChangesModalOpen}
        cancel={() => setUnsavedChangesModalOpen(false)}
        confirm={handleConfirmClose}
        confirmButtonText="Close"
        confirmButtonType="danger"
        message="You have unsaved changes. Are you sure you want to close?"
      />
    </>
  )
}

export default ExportMapModal
